// RETORNO A CONSIGNATARIA, FORMATO DETALHADO (#189) — o padrao de todo credor
// que nao tem formato proprio, e das rubricas sem consignataria vinculada.
// Nasceu como a versao 2.0 do `assalce-retorno` (30/09/2026); em 01/10/2026 a
// ASSALCE pediu de volta as quatro colunas, e o formato detalhado ficou para os
// demais.
//
// TXT sem cabecalho, `;`, ASCII, CRLF, uma linha por CONTRATO de cada linha do
// retorno do ente nas rubricas do credor — inclusive a que nao teve desconto:
//
//   matricula;cpf;nome;verba;contrato;valorInstruido;valorDescontado;motivo
//   001476;12345678909;MARIA DA SILVA;807;ADF-1;250,00;250,00;PGTO OK
//   001476;12345678909;MARIA DA SILVA;807;ADF-2;250,00;0,00;MARGEM INSUFICIENTE
//
// 2.0 (01/10/2026): o NOME do servidor depois do CPF — o cliente pediu ao menos
// nome, CPF e valor consignado (o descontado) — e o arquivo com o nome do
// credor: `RE{CREDOR}{AAAAMM}.txt`, `REBRASIL202609.txt` para o Banco do Brasil;
// `RECONSIG{AAAAMM}.txt` para as rubricas sem consignataria vinculada.
//
// Tudo o que esta na folha vai: desligado, falecido e margem insuficiente saem
// com 0,00 e o MOTIVO da folha. A folha nao traz nome: ele vem do cadastro de
// servidores, e a matricula sem cadastro sai com a coluna vazia (o relatorio
// acusa).
//
// Matricula e verba saem como o ente as escreveu na folha. O contrato e' a ADF
// da Atlas, so' no credito: a nao creditícia (plano, convenio) e' um desconto
// por matricula e verba, sem ADF, e sai escrita `SEM ADF`. O credito que a
// Atlas nao tem como nomear sai VAZIO, com a linha da folha inteira, e o
// relatorio acusa — as duas situacoes nao se confundem no arquivo. Quem separa
// por contrato e' a API (a conciliacao decide o rateio); aqui e' so' a forma.
import { layoutDoCatalogo } from "../catalogo.js";
import { cpf11, juntarLinhas, nomeDoCredorNoArquivo, reais, textoLivre } from "./forma-retorno.js";
const CODIGO = "consignataria-retorno";
const SEPARADOR = ";";
/** A coluna contrato da consignacao nao creditícia, que nao tem ADF. */
const SEM_ADF = "SEM ADF";
/** O arquivo das rubricas sem consignataria vinculada, que nao tem credor para dar nome. */
const SEM_CREDOR = "CONSIG";
function versao() {
    return layoutDoCatalogo(CODIGO).versao;
}
function colunaContrato(l) {
    return l.credito ? textoLivre(l.contrato ?? "") : SEM_ADF;
}
export const adaptadorConsignatariaRetorno = {
    codigo: CODIGO,
    serializar(entrada) {
        // Ordenacao estavel: os contratos da mesma linha da folha ficam na ordem
        // em que a API os entregou.
        const ordenadas = [...entrada.linhas].sort((a, b) => a.linha - b.linha);
        const zeradas = ordenadas.filter((l) => l.descontadoCentavos <= 0).length;
        // So' o credito tem contrato a nomear: a nao creditícia sem ADF e' o esperado.
        const semContrato = ordenadas.filter((l) => l.credito && !l.contrato).length;
        const semNome = ordenadas.filter((l) => !textoLivre(l.nome)).length;
        let cpfsCompletados = 0;
        const linhas = ordenadas.map((l) => {
            const c = cpf11(l.cpf);
            if (c.completado)
                cpfsCompletados++;
            return [
                l.matricula, c.valor, textoLivre(l.nome), l.rubrica, colunaContrato(l),
                reais(l.instruidoCentavos), reais(l.descontadoCentavos), textoLivre(l.motivo),
            ].join(SEPARADOR);
        });
        const avisos = [];
        if (ordenadas.length === 0)
            avisos.push("sem linhas nas rubricas do credor nesta competência: o arquivo sai vazio");
        if (zeradas > 0)
            avisos.push(`${zeradas} linha(s) sem desconto saem com 0,00 e o motivo da folha`);
        if (semContrato > 0) {
            avisos.push(`${semContrato} linha(s) sem contrato identificado na Atlas: saem com a coluna contrato vazia e o valor da folha inteiro`);
        }
        if (semNome > 0) {
            avisos.push(`${semNome} linha(s) de matrícula sem cadastro na Atlas: saem com a coluna nome vazia`);
        }
        if (cpfsCompletados > 0)
            avisos.push(`${cpfsCompletados} CPF(s) vieram curtos e foram completados com zeros a esquerda`);
        // A mesma pessoa, verba e contrato mais de uma vez (duas folhas, ou dois
        // descontos nao creditícios da mesma verba): as linhas saem, porque o
        // arquivo nao tem coluna de folha para distingui-las.
        const repetidas = new Map();
        for (const l of ordenadas) {
            const k = [l.matricula, l.rubrica, colunaContrato(l)].join(SEPARADOR);
            repetidas.set(k, (repetidas.get(k) ?? 0) + 1);
        }
        for (const [k, n] of repetidas) {
            if (n > 1)
                avisos.push(`matrícula, verba e contrato ${k} aparecem em ${n} linha(s): mais de uma folha para a mesma pessoa`);
        }
        const semCorrespondencia = [];
        if (cpfsCompletados > 0)
            semCorrespondencia.push("cpf");
        if (semNome > 0)
            semCorrespondencia.push("nome");
        if (semContrato > 0)
            semCorrespondencia.push("contrato");
        const relatorio = {
            layout: { codigo: CODIGO, versao: versao() },
            competencia: entrada.competencia,
            linhas: linhas.length,
            dividasInstruidas: ordenadas.length,
            dividasSomadas: [],
            identificadorEscrito: true,
            camposSemCorrespondencia: semCorrespondencia,
            totaisPorCredor: [],
            avisos,
        };
        return {
            arquivos: [{
                    nome: this.nomeArquivo(entrada.competencia, entrada.destinatario),
                    texto: juntarLinhas(linhas),
                    codificacao: "utf-8",
                    contentType: "text/plain; charset=us-ascii",
                }],
            relatorio,
        };
    },
    /**
     * `RE` + o nome do credor sem espacos + `AAAAMM`. Sem credor (as rubricas
     * sem consignataria vinculada) e quando o nome nao deixa nada, `RECONSIG`.
     */
    nomeArquivo(competencia, destinatario) {
        const credor = destinatario ? nomeDoCredorNoArquivo(destinatario) : null;
        return `RE${credor ?? SEM_CREDOR}${competencia}.txt`;
    },
};
/**
 * O MESMO arquivo como planilha (01/10/2026): o credor pode baixar em Excel em
 * vez de TXT. O registro guardado continua sendo o TXT (e' ele que tem hash);
 * a planilha e' derivada dele na hora do download, e por isso parte do texto
 * gerado, nao da entrada — o que esta' no TXT e' o que vai na planilha.
 *
 * Primeira linha e' o cabecalho, com o nome de cada coluna como o catalogo as
 * declara. Toda celula e' TEXTO: o CPF guarda o zero a esquerda, a matricula
 * `000029` nao vira 29, e o valor fica `4515,11` como no TXT. Quem escreve o
 * xlsx e' a borda (ADR-0007: o dominio nao sabe de formato binario).
 */
export function celulasDoRetornoDetalhado(textoDoArquivo) {
    const cabecalho = layoutDoCatalogo(CODIGO).arquivos[0].colunas.map((c) => c.nome);
    const linhas = textoDoArquivo.split(/\r?\n/).filter((l) => l.length > 0).map((l) => l.split(SEPARADOR));
    return [cabecalho, ...linhas];
}
//# sourceMappingURL=consignataria-retorno.js.map